import {
    ResolvedRecord,
    ResolvedSubmission,
    SubmissionsEnabledStatus,
} from '../../types/submissions';

import { queryOptions } from '@tanstack/react-query';
import { ApiError } from '~/components/Providers/APIClient';
import { apiQueryFn } from '../utils';
import { submissionsParamsType } from '~/routes/_authenticated/staff/_submissionsLayout/submissions/index/-SearchParams';
import { PaginatedResponse } from '~/util/types/api';
import { recordsParamsType } from '~/routes/_authenticated/staff/_recordsLayout/records/index/-SearchParams';

export type submissionsQueryParams = {
    list: string;
    filters?: submissionsParamsType;
};

export const submissionsQuery = ({ list, filters }: submissionsQueryParams) =>
    queryOptions<PaginatedResponse<ResolvedSubmission>, ApiError>({
        queryKey: [
            'submissions',
            list,
            filters?.level?.id,
            filters?.submitter?.id,
            filters?.status,
            filters?.reviewer?.id,
            filters?.priority,
            filters?.notes,
            filters?.mobile,
            filters?.page,
            filters?.per_page,
            filters?.sort,
            filters?.reverseSort,
        ],
        queryFn: apiQueryFn<PaginatedResponse<ResolvedSubmission>>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/submissions`,
            attachAuth: true,
            query: {
                status_filter: filters?.status,
                mobile_filter: filters?.mobile,
                level_filter: filters?.level?.id,
                submitter_filter: filters?.submitter?.id,
                priority_filter: filters?.priority,
                page: filters?.page,
                per_page: filters?.per_page,
                reviewer_filter: filters?.reviewer?.id,
                note_filter: filters?.notes ? `%${filters.notes}%` : null,
                sort: filters?.sort
                    ? filters.sort === 'CompletionTime'
                        ? `${filters.reverseSort ? 'Longest' : 'Shortest'}CompletionTime`
                        : `${filters.reverseSort ? 'Oldest' : 'Newest'}${filters.sort}`
                    : undefined,
            },
        }),
    });

export type submissionStatusFullQueryParams = {
    list: string;
    filters?: submissionsParamsType;
};

export const submissionStatusFullQuery = ({
    list,
}: submissionStatusFullQueryParams) => ({
    queryKey: ['submissions_status_full', list],
    queryFn: apiQueryFn<SubmissionsEnabledStatus>({
        path: `/${list === 'classic' ? 'aredl' : 'arepl'}/submissions/status/full`,
        attachAuth: true,
    }),
});

export type submissionQueryParams = {
    list: string;
    id: string;
};

export const submissionQuery = ({ list, id }: submissionQueryParams) => ({
    queryKey: ['submission', list, id],
    queryFn: apiQueryFn<ResolvedSubmission>({
        path: `/${list === 'classic' ? 'aredl' : 'arepl'}/submissions/${id}`,
        attachAuth: true,
    }),
});

export type recordsQueryParams = {
    list: string;
    filters?: recordsParamsType;
};

export const recordsQuery = ({ list, filters }: recordsQueryParams) =>
    queryOptions<PaginatedResponse<ResolvedRecord>, ApiError>({
        queryKey: [
            'records',
            list,
            filters?.level?.id,
            filters?.submitter?.id,
            filters?.mobile,
            filters?.verification,
            filters?.page,
            filters?.per_page,
            filters?.sort,
            filters?.reverseSort,
        ],
        queryFn: apiQueryFn<PaginatedResponse<ResolvedRecord>>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/records`,
            attachAuth: true,
            query: {
                mobile_filter: filters?.mobile,
                verification_filter: filters?.verification,
                level_filter: filters?.level?.id,
                submitter_filter: filters?.submitter?.id,
                page: filters?.page,
                per_page: filters?.per_page,
                sort: filters?.sort
                    ? filters.sort === 'CompletionTime'
                        ? `${filters.reverseSort ? 'Longest' : 'Shortest'}CompletionTime`
                        : `${filters.reverseSort ? 'Oldest' : 'Newest'}${filters.sort}`
                    : undefined,
            },
        }),
    });

export type recordQueryParams = {
    list: string;
    id: string;
};

export const recordQuery = ({ list, id }: recordQueryParams) => ({
    queryKey: ['record', list, id],
    queryFn: apiQueryFn<ResolvedRecord>({
        path: `/${list === 'classic' ? 'aredl' : 'arepl'}/records/${id}`,
        attachAuth: true,
    }),
});
